import type {
  AdminLookupResponse,
  AdminStatusResponse,
  StockHoldSkipRequest,
  StockHoldSkipResponse,
} from "~/types/admin-status";
import type {
  AddAdminRequest,
  AddAdminResponse,
  AdminUsersResponse,
  RevokeAdminRequest,
  RevokeAdminResponse,
} from "~/types/admin-users";
import type {
  DisasterEvent,
  DisasterEventRequest,
  MarkReturnedRequest,
  MarkReturnedResult,
  PendingRefundsResponse,
  RefundExecuteRequest,
  RefundExecuteResult,
  ReleaseHoldResult,
  ReplacementShipmentRequest,
  ReplacementShipmentResult,
  StopOperationRequest,
  StopOperationResult,
} from "~/types/admin-disaster";
import type { OperationLogsResponse } from "~/types/admin-operation-logs";
import type { AdminCyclesResponse } from "~/types/admin-cycles";
import type {
  PendingShipmentsResponse,
  ShipmentAcceptanceRequest,
  ShipmentAcceptanceResult,
  ShipmentImportRequest,
  ShipmentImportResult,
  ShipmentImportRow,
} from "~/types/admin-shipping";
import type {
  AdminInventoryResponse,
  CorrectStockCapacityRequest,
  CorrectStockCapacityResponse,
  RecordPhysicalShortageRequest,
  RecordPhysicalShortageResponse,
  SetCampaignQuotaCapacityRequest,
  SetCampaignQuotaCapacityResponse,
  SetStockAcceptingRequest,
  SetStockAcceptingResponse,
} from "~/types/admin-inventory";

interface ErrorResponse {
  error?: {
    code?: string;
    message?: string;
  };
}

export class AdminApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = "AdminApiError";
    this.status = status;
    this.code = code;
  }
}

async function readError(response: Response): Promise<ErrorResponse | null> {
  try {
    return (await response.json()) as ErrorResponse;
  } catch {
    return null;
  }
}

function apiBaseUrl(): string {
  const value = import.meta.env.VITE_API_BASE_URL?.trim().replace(/\/$/, "");
  if (!value) {
    throw new AdminApiError(
      0,
      "VITE_API_BASE_URL が設定されていません。",
      "missing_api_base_url",
    );
  }
  return value;
}

interface AdminRequestOptions {
  method?: "GET" | "POST";
  body?: unknown;
  signal?: AbortSignal;
  failureMessage: string;
}

async function adminRequest<T>(
  path: string,
  accessToken: string,
  { method = "GET", body, signal, failureMessage }: AdminRequestOptions,
): Promise<T> {
  const baseUrl = apiBaseUrl();

  let response: Response;
  try {
    response = await fetch(`${baseUrl}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
        ...(body === undefined ? {} : { "Content-Type": "application/json" }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error;
    }

    throw new AdminApiError(
      0,
      "API に接続できませんでした。しばらくしてから再試行してください。",
      "network_error",
    );
  }

  if (!response.ok) {
    const responseBody = await readError(response);
    throw new AdminApiError(
      response.status,
      responseBody?.error?.message ??
        `${failureMessage}（${response.status}）。`,
      responseBody?.error?.code,
    );
  }

  try {
    return (await response.json()) as T;
  } catch {
    throw new AdminApiError(
      502,
      "API から不正なレスポンスを受信しました。",
      "invalid_response",
    );
  }
}

export async function fetchAdminStatus(
  accessToken: string,
  signal?: AbortSignal,
): Promise<AdminStatusResponse> {
  return adminRequest<AdminStatusResponse>("/admin/status", accessToken, {
    signal,
    failureMessage: "状況の取得に失敗しました",
  });
}

export async function fetchAdminLookup(
  accessToken: string,
  q: string,
  signal?: AbortSignal,
): Promise<AdminLookupResponse> {
  return adminRequest<AdminLookupResponse>(
    `/admin/lookup?q=${encodeURIComponent(q)}`,
    accessToken,
    {
      signal,
      failureMessage: "決済照会に失敗しました",
    },
  );
}

export async function fetchAdminInventory(
  accessToken: string,
  query: string,
  signal?: AbortSignal,
): Promise<AdminInventoryResponse> {
  return adminRequest<AdminInventoryResponse>(
    `/admin/inventory${query}`,
    accessToken,
    {
      signal,
      failureMessage: "在庫・枠の取得に失敗しました",
    },
  );
}

export async function setCampaignQuotaCapacity(
  accessToken: string,
  request: SetCampaignQuotaCapacityRequest,
  signal?: AbortSignal,
): Promise<SetCampaignQuotaCapacityResponse> {
  return adminRequest<SetCampaignQuotaCapacityResponse>(
    "/admin/campaign-quotas/capacity",
    accessToken,
    {
      method: "POST",
      body: request,
      signal,
      failureMessage: request.dry_run
        ? "変更内容のプレビューに失敗しました"
        : "月次枠の確定に失敗しました",
    },
  );
}

export async function recordPhysicalShortage(
  accessToken: string,
  request: RecordPhysicalShortageRequest,
  signal?: AbortSignal,
): Promise<RecordPhysicalShortageResponse> {
  return adminRequest<RecordPhysicalShortageResponse>(
    "/admin/stock-pools/shortage",
    accessToken,
    {
      method: "POST",
      body: request,
      signal,
      failureMessage: request.dry_run
        ? "物理不足のプレビューに失敗しました"
        : "物理不足の記録に失敗しました",
    },
  );
}

export async function correctStockCapacity(
  accessToken: string,
  request: CorrectStockCapacityRequest,
  signal?: AbortSignal,
): Promise<CorrectStockCapacityResponse> {
  return adminRequest<CorrectStockCapacityResponse>(
    "/admin/stock-pools/capacity",
    accessToken,
    {
      method: "POST",
      body: request,
      signal,
      failureMessage: request.dry_run
        ? "容量補正のプレビューに失敗しました"
        : "容量補正の確定に失敗しました",
    },
  );
}

export async function setStockAccepting(
  accessToken: string,
  request: SetStockAcceptingRequest,
  signal?: AbortSignal,
): Promise<SetStockAcceptingResponse> {
  return adminRequest<SetStockAcceptingResponse>(
    "/admin/stock-pools/accepting",
    accessToken,
    {
      method: "POST",
      body: request,
      signal,
      failureMessage: request.dry_run
        ? "受付変更のプレビューに失敗しました"
        : "受付変更の確定に失敗しました",
    },
  );
}

export async function skipStockHeldCycles(
  accessToken: string,
  request: StockHoldSkipRequest,
  signal?: AbortSignal,
): Promise<StockHoldSkipResponse> {
  return adminRequest<StockHoldSkipResponse>(
    "/admin/stock-holds/skip",
    accessToken,
    {
      method: "POST",
      body: request,
      signal,
      failureMessage: "スキップ確定に失敗しました",
    },
  );
}

export async function fetchAdmins(
  accessToken: string,
  signal?: AbortSignal,
): Promise<AdminUsersResponse> {
  return adminRequest<AdminUsersResponse>("/admin/admins", accessToken, {
    signal,
    failureMessage: "管理者一覧の取得に失敗しました",
  });
}

export async function addAdmin(
  accessToken: string,
  request: AddAdminRequest,
  signal?: AbortSignal,
): Promise<AddAdminResponse> {
  return adminRequest<AddAdminResponse>("/admin/admins", accessToken, {
    method: "POST",
    body: request,
    signal,
    failureMessage: "管理者の追加に失敗しました",
  });
}

export async function revokeAdmin(
  accessToken: string,
  request: RevokeAdminRequest,
  signal?: AbortSignal,
): Promise<RevokeAdminResponse> {
  return adminRequest<RevokeAdminResponse>("/admin/users/role", accessToken, {
    method: "POST",
    body: request,
    signal,
    failureMessage: "管理者権限の解除に失敗しました",
  });
}

export async function fetchPendingShipments(
  accessToken: string,
  signal?: AbortSignal,
): Promise<PendingShipmentsResponse> {
  return adminRequest<PendingShipmentsResponse>(
    "/admin/shipments/pending",
    accessToken,
    {
      signal,
      failureMessage: "未処理一覧の取得に失敗しました",
    },
  );
}

export async function importShipments(
  accessToken: string,
  request: ShipmentImportRequest,
  signal?: AbortSignal,
): Promise<ShipmentImportResult> {
  return adminRequest<ShipmentImportResult>(
    "/admin/shipments/import",
    accessToken,
    {
      method: "POST",
      body: request,
      signal,
      failureMessage: request.dry_run
        ? "取込プレビューに失敗しました"
        : "追跡番号の一括登録に失敗しました",
    },
  );
}

export async function registerShipment(
  accessToken: string,
  request: ShipmentImportRow,
  signal?: AbortSignal,
): Promise<ShipmentImportResult> {
  return adminRequest<ShipmentImportResult>("/admin/shipments", accessToken, {
    method: "POST",
    body: request,
    signal,
    failureMessage: "追跡番号の登録に失敗しました",
  });
}

export async function recordShipmentAcceptance(
  accessToken: string,
  request: ShipmentAcceptanceRequest,
  signal?: AbortSignal,
): Promise<ShipmentAcceptanceResult> {
  return adminRequest<ShipmentAcceptanceResult>(
    "/admin/shipments/acceptance",
    accessToken,
    {
      method: "POST",
      body: request,
      signal,
      failureMessage: "引受記録に失敗しました",
    },
  );
}

export async function createDisasterEvent(
  accessToken: string,
  request: DisasterEventRequest,
  signal?: AbortSignal,
): Promise<DisasterEvent> {
  return adminRequest<DisasterEvent>("/admin/disaster-events", accessToken, {
    method: "POST",
    body: request,
    signal,
    failureMessage: "災害事象の作成に失敗しました",
  });
}

export async function executeStopOperation(
  accessToken: string,
  eventId: string,
  request: StopOperationRequest,
  signal?: AbortSignal,
): Promise<StopOperationResult> {
  return adminRequest<StopOperationResult>(
    `/admin/disaster-events/${encodeURIComponent(eventId)}/stop`,
    accessToken,
    {
      method: "POST",
      body: request,
      signal,
      failureMessage: request.dry_run
        ? "災害停止のプレビューに失敗しました"
        : "災害停止の確定に失敗しました",
    },
  );
}

export async function fetchPendingRefunds(
  accessToken: string,
  signal?: AbortSignal,
): Promise<PendingRefundsResponse> {
  return adminRequest<PendingRefundsResponse>(
    "/admin/refunds/pending",
    accessToken,
    {
      signal,
      failureMessage: "返金キューの取得に失敗しました",
    },
  );
}

export async function executeRefunds(
  accessToken: string,
  request: RefundExecuteRequest,
  signal?: AbortSignal,
): Promise<RefundExecuteResult> {
  return adminRequest<RefundExecuteResult>("/admin/refunds", accessToken, {
    method: "POST",
    body: request,
    signal,
    failureMessage: "返金実行に失敗しました",
  });
}

export async function releaseDisasterHold(
  accessToken: string,
  subscriptionId: string,
  reason: string,
  signal?: AbortSignal,
): Promise<ReleaseHoldResult> {
  return adminRequest<ReleaseHoldResult>(
    `/admin/subscriptions/${encodeURIComponent(subscriptionId)}/release-hold`,
    accessToken,
    {
      method: "POST",
      body: { reason },
      signal,
      failureMessage: "個別解除に失敗しました",
    },
  );
}

export async function markReturned(
  accessToken: string,
  request: MarkReturnedRequest,
  signal?: AbortSignal,
): Promise<MarkReturnedResult> {
  return adminRequest<MarkReturnedResult>("/admin/returns", accessToken, {
    method: "POST",
    body: request,
    signal,
    failureMessage: "未受取終了に失敗しました",
  });
}

export async function registerReplacementShipment(
  accessToken: string,
  request: ReplacementShipmentRequest,
  signal?: AbortSignal,
): Promise<ReplacementShipmentResult> {
  return adminRequest<ReplacementShipmentResult>(
    "/admin/shipments/replacement",
    accessToken,
    {
      method: "POST",
      body: request,
      signal,
      failureMessage: "交換出荷の登録に失敗しました",
    },
  );
}

export async function fetchOperationLogs(
  accessToken: string,
  query: string,
  signal?: AbortSignal,
): Promise<OperationLogsResponse> {
  return adminRequest<OperationLogsResponse>(
    `/admin/operation-logs${query}`,
    accessToken,
    {
      signal,
      failureMessage: "操作ログの取得に失敗しました",
    },
  );
}

export async function fetchAdminCycles(
  accessToken: string,
  query: string,
  signal?: AbortSignal,
): Promise<AdminCyclesResponse> {
  return adminRequest<AdminCyclesResponse>(
    `/admin/cycles${query}`,
    accessToken,
    {
      signal,
      failureMessage: "定期便の回の取得に失敗しました",
    },
  );
}

export async function downloadShippingList(
  accessToken: string,
  windowStart: string,
  signal?: AbortSignal,
): Promise<Blob> {
  let response: Response;
  try {
    response = await fetch(
      `${apiBaseUrl()}/admin/shipping-list?window_start=${encodeURIComponent(windowStart)}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: "text/csv",
        },
        signal,
      },
    );
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error;
    }
    throw new AdminApiError(
      0,
      "API に接続できませんでした。しばらくしてから再試行してください。",
      "network_error",
    );
  }

  if (!response.ok) {
    const responseBody = await readError(response);
    throw new AdminApiError(
      response.status,
      responseBody?.error?.message ??
        `出荷リストのダウンロードに失敗しました（${response.status}）。`,
      responseBody?.error?.code,
    );
  }

  return response.blob();
}
